import { motion } from 'framer-motion';
import { ArrowUpRight, RotateCcw, Box, Wrench, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import cncTurning from '@/assets/cnc-turning-new.webp';
import cncMilling from '@/assets/cnc-milling-new.webp';
import baugruppen from '@/assets/baugruppen.webp';
import cadCam from '@/assets/cad-cam.webp';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/lib/i18n';

export function ServicesPreview() {
  const t = useT();
  const altText: Record<string, string> = {
    'CNC-Drehen': 'CNC-Drehen Fertigung von Präzisionsdrehteilen auf Emco TURN E65',
    'CNC Turning': 'CNC turning production of precision turned parts on Emco TURN E65',
    'CNC-Fräsen': 'CNC-Fräsen 5-Achs-Bearbeitung auf HURCO Bearbeitungszentrum',
    'CNC Milling': 'CNC milling 5-axis machining on HURCO machining centre',
    'Baugruppen': 'Baugruppen Montage und Komplettfertigung aus einer Hand',
    'Assemblies': 'Assemblies montage and complete manufacturing from a single source',
    'CAD/CAM': 'CAD/CAM Konstruktion und Programmierung mit SolidWorks',
  };
  const services = [
    {
      icon: RotateCcw,
      title: t('CNC-Drehen', 'CNC Turning'),
      description: t(
        'Hochpräzise Drehteile von Ø25 bis Ø200mm mit angetriebenen Werkzeugen und Y-Achse.',
        'High-precision turned parts from Ø25 to Ø200 mm with driven tools and Y-axis.',
      ),
      specs: [
        'Emco TURN E65',
        t('Stangenlader bis Ø65', 'Bar feeder up to Ø65'),
        t('Komplettbearbeitung', 'Complete machining'),
      ],
      image: cncTurning,
    },
    {
      icon: Box,
      title: t('CNC-Fräsen', 'CNC Milling'),
      description: t(
        'Komplexe 3D-Geometrien mit 4. und 5. Achse auf modernsten HURCO Bearbeitungszentren.',
        'Complex 3D geometries with 4th and 5th axis on state-of-the-art HURCO machining centres.',
      ),
      specs: [
        'HURCO VMX 30i/42',
        t('Verfahrweg bis 1060mm', 'Travel up to 1060 mm'),
        t('5-Achs-Bearbeitung', '5-axis machining'),
      ],
      image: cncMilling,
    },
    {
      icon: Layers,
      title: t('Baugruppen', 'Assemblies'),
      description: t(
        'Von der Einzelkomponente zur fertigen Baugruppe. Wir bieten alles aus einer Hand.',
        'From single components to finished assemblies — everything from a single source.',
      ),
      specs: [
        t('Konstruktion', 'Design'),
        t('Montage', 'Assembly'),
        t('Dokumentation', 'Documentation'),
      ],
      image: baugruppen,
    },
    {
      icon: Wrench,
      title: 'CAD/CAM',
      description: t(
        'Professionelle Konstruktion und Programmierung mit SolidWorks für optimale Ergebnisse.',
        'Professional design and programming with SolidWorks for optimal results.',
      ),
      specs: ['SolidWorks', t('3D-Modellierung', '3D modelling'), t('Simulation', 'Simulation')],
      image: cadCam,
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-background to-card">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span
            className="text-primary font-mono text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('Unser Leistungsspektrum', 'Our service portfolio')}
          </motion.span>
          <motion.h2
            className="mt-4 text-4xl md:text-5xl font-bold"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            {t('Zerspanung auf ', 'Machining at the ')}
            <span className="text-primary">{t('höchstem Niveau', 'highest level')}</span>
          </motion.h2>
          <motion.p
            className="mt-4 text-lg text-muted-foreground"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {t(
              'Stahl, Edelstahl, Aluminium, NE-Metalle und Kunststoffe – wir bearbeiten alle Materialien mit höchster Präzision.',
              'Steel, stainless steel, aluminium, non-ferrous metals and plastics — we machine every material with the highest precision.',
            )}
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className="group relative overflow-hidden rounded-xl border border-border/50 bg-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              {/* Background Image – more prominent */}
              <div className="absolute inset-0 opacity-20 group-hover:opacity-35 transition-opacity duration-500">
                <img
                  src={service.image}
                  alt={altText[service.title] ?? service.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  draggable={false}
                />
              </div>

              {/* Content */}
              <div className="relative p-8">
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <service.icon className="w-7 h-7 text-primary" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>

                <h3 className="mt-6 text-2xl font-bold group-hover:text-primary transition-colors">
                  {service.title}
                </h3>
                <p className="mt-3 text-muted-foreground">
                  {service.description}
                </p>

                {/* Specs */}
                <div className="mt-6 flex flex-wrap gap-2">
                  {service.specs.map((spec) => (
                    <span
                      key={spec}
                      className="spec-badge technical-data"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>

              {/* Hover Border */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary/30 rounded-xl transition-colors pointer-events-none" />
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Button variant="steel" size="lg" asChild>
            <LocalizedLink to="/leistungen">
              {t('Alle Leistungen entdecken', 'Explore all services')}
              <ArrowUpRight className="w-4 h-4" />
            </LocalizedLink>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
